import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	fetchConfluenceAuthorHydrationQuery,
	type ConfluenceHydratedAuthorUser,
} from '@atlassian/search-client/confluence-author-hydration';

import type { PrimaryProductKey } from '../../../common/constants/product-keys';

type SearchResultEdge =
	| {
			readonly node?: {
				readonly __typename?: string | null;
				readonly id?: string | null;
				readonly type?: string | null;
			} | null;
	  }
	| null
	| undefined;

export type ConfluenceAuthorHydrationIds = {
	pageIds: string[];
	blogPostIds: string[];
};

export const getConfluenceAuthorHydrationIds = (
	edges: ReadonlyArray<SearchResultEdge>,
): ConfluenceAuthorHydrationIds => {
	const pageIds = new Set<string>();
	const blogPostIds = new Set<string>();

	for (const edge of edges) {
		const node = edge?.node;
		if (node?.__typename !== 'SearchConfluencePageBlogAttachment' || !node.id) {
			continue;
		}

		switch (node.type?.toLowerCase()) {
			case 'page':
				pageIds.add(node.id);
				break;
			case 'blogpost':
				blogPostIds.add(node.id);
				break;
		}
	}

	return {
		pageIds: Array.from(pageIds),
		blogPostIds: Array.from(blogPostIds),
	};
};

type FetchConfluenceAuthorsProps = ConfluenceAuthorHydrationIds & {
	cloudId?: string;
	aggAbsoluteUrl?: string;
	logError: (error: unknown) => void;
	product?: PrimaryProductKey;
};

export const fetchConfluenceAuthors = async ({
	pageIds,
	blogPostIds,
	cloudId,
	aggAbsoluteUrl,
	logError,
	product,
}: FetchConfluenceAuthorsProps): Promise<ConfluenceHydratedAuthorUser[]> => {
	if (pageIds.length === 0 && blogPostIds.length === 0) {
		return [];
	}

	try {
		const response = await fetchConfluenceAuthorHydrationQuery({
			variables: {
				pageIds,
				blogPostIds,
				includePages: pageIds.length > 0,
				includeBlogPosts: blogPostIds.length > 0,
			},
			aggAbsoluteUrl,
			headers: buildAtlAttributionHeaders({ cloudId, product }),
		});

		if (response.errors?.length) {
			logError(response.errors);
		}

		const confluence = response.data?.confluence;
		return [...(confluence?.pages ?? []), ...(confluence?.blogPosts ?? [])].flatMap((content) =>
			content?.author?.user ? [content.author.user] : [],
		);
	} catch (error) {
		logError(error);
		return [];
	}
};
